/**
 * Skip Cart - Intercepts product form submissions with data-skip-cart attribute
 * Opens the cart drawer instead of redirecting to the cart page
 */

class SkipCart {
  constructor() {
    this.bindEvents();
  }

  bindEvents() {
    // Listen for all form submissions
    document.addEventListener('submit', this.handleFormSubmit.bind(this));
    
    // Listen for click events on buttons with data-skip-cart-button attribute
    document.addEventListener('click', this.handleSkipCartButtonClick.bind(this));
  }

  handleFormSubmit(event) {
    const form = event.target;
    // The product-form custom element already submits and renders the drawer.
    if (form.closest('product-form')) return;
    
    // Only intercept product forms with skip-cart enabled
    if (!form.action.includes('/cart/add') || !form.querySelector('[data-skip-cart="true"]')) {
      return;
    }
    
    // Prevent default form submission
    event.preventDefault();
    
    // Get product data from the form
    const formData = new FormData(form);
    
    // Show loading state on submit button
    const submitButton = form.querySelector('[type="submit"]');
    if (submitButton) {
      submitButton.classList.add('loading');
      const loadingOverlay = submitButton.querySelector('.loading-overlay__spinner');
      if (loadingOverlay) loadingOverlay.classList.remove('hidden');
    }
    
    // Add the product to the cart via AJAX
    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      body: formData
    })
    .then(async response => {
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'Error adding product to cart');
      return data;
    })
    .then(data => {
      // Get updated cart sections
      fetch(window.location.pathname + '?sections=cart-drawer,cart-icon-bubble')
        .then(response => response.json())
        .then(sections => {
          // Open the cart drawer with the updated content
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer) {
            cartDrawer.renderContents({
              sections: sections,
              id: data.id || data.variant_id
            });
          } else {
            // Fallback to cart notification if cart drawer doesn't exist
            const cartNotification = document.querySelector('cart-notification');
            if (cartNotification) {
              cartNotification.renderContents({
                sections: sections,
                key: data.key
              });
            }
          }
        })
        .catch(error => {
          console.error('Error fetching cart sections:', error);
          // Fallback to cart page if there's an error
          window.location.href = window.Shopify.routes.root + 'cart';
        })
        .finally(() => {
          // Reset loading state
          if (submitButton) {
            submitButton.classList.remove('loading');
            const loadingOverlay = submitButton.querySelector('.loading-overlay__spinner');
            if (loadingOverlay) loadingOverlay.classList.add('hidden');
          }
        });
    })
    .catch(error => {
      console.error('Error adding product to cart:', error);
      // Show error message
      const errorMessageWrapper = form.querySelector('.product-form__error-message-wrapper');
      if (errorMessageWrapper) {
        const errorMessage = errorMessageWrapper.querySelector('.product-form__error-message');
        errorMessage.textContent = error.message || 'Error adding product to cart';
        errorMessageWrapper.removeAttribute('hidden');
        
        setTimeout(() => {
          errorMessageWrapper.setAttribute('hidden', '');
        }, 5000);
      }
      
      // Reset loading state
      if (submitButton) {
        submitButton.classList.remove('loading');
        const loadingOverlay = submitButton.querySelector('.loading-overlay__spinner');
        if (loadingOverlay) loadingOverlay.classList.add('hidden');
      }
    });
  }

  handleSkipCartButtonClick(event) {
    // Check if the clicked element is a skip cart button
    const skipCartButton = event.target.closest('[data-skip-cart-button]');
    if (!skipCartButton) return;
    
    // Prevent default click behavior
    event.preventDefault();
    
    // Find the closest product form
    const form = skipCartButton.closest('form[action*="/cart/add"]');
    if (!form) return;
    
    // Show loading state on the button
    skipCartButton.classList.add('loading');
    const loadingOverlay = skipCartButton.querySelector('.loading-overlay__spinner');
    if (loadingOverlay) loadingOverlay.classList.remove('hidden');
    
    // Get product data from the form
    const formData = new FormData(form);
    
    // Add the product to the cart via AJAX
    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      body: formData
    })
    .then(async response => {
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'Error adding product to cart');
      return data;
    })
    .then(data => {
      // Get updated cart sections
      fetch(window.location.pathname + '?sections=cart-drawer,cart-icon-bubble')
        .then(response => response.json())
        .then(sections => {
          // Open the cart drawer with the updated content
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer) {
            cartDrawer.renderContents({
              sections: sections,
              id: data.id || data.variant_id
            });
          } else {
            // Fallback to cart notification if cart drawer doesn't exist
            const cartNotification = document.querySelector('cart-notification');
            if (cartNotification) {
              cartNotification.renderContents({
                sections: sections,
                key: data.key
              });
            }
          }
        })
        .catch(error => {
          console.error('Error fetching cart sections:', error);
          // Fallback to cart page if there's an error
          window.location.href = window.Shopify.routes.root + 'cart';
        })
        .finally(() => {
          // Reset loading state
          skipCartButton.classList.remove('loading');
          if (loadingOverlay) loadingOverlay.classList.add('hidden');
        });
    })
    .catch(error => {
      console.error('Error adding product to cart:', error);
      
      // Reset loading state
      skipCartButton.classList.remove('loading');
      if (loadingOverlay) loadingOverlay.classList.add('hidden');
      
      // Show error message if possible
      const errorMessageWrapper = form.querySelector('.product-form__error-message-wrapper');
      if (errorMessageWrapper) {
        const errorMessage = errorMessageWrapper.querySelector('.product-form__error-message');
        errorMessage.textContent = error.message || 'Error adding product to cart';
        errorMessageWrapper.removeAttribute('hidden');
        
        setTimeout(() => {
          errorMessageWrapper.setAttribute('hidden', '');
        }, 5000);
      }
    });
  }
}

// Initialize the SkipCart functionality when the DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new SkipCart();
}); 
